// import { Quote as QuoteModel } from '@leads/functions/quotes/quote';
// Update the import path below to the correct relative location of the Quote model:
import { useEffect, useMemo, useState } from 'react';
import { Quote as QuoteModel } from '@leads/functions/src/quotes/quote';
import type { QuoteData, QuoteOption } from '@leads/functions/src/quotes/quote';
import { IconCheck } from '@tabler/icons-react';
import { formatInTimeZone } from 'date-fns-tz';
import { useSearchParams } from 'react-router';
import {
  Button,
  Grid,
  Group,
  List,
  NumberFormatter,
  Stack,
  Table,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { refreshImport } from '@/data/imports';
import { type IImport } from '@/types/import';
import classes from './Quote.module.css';

const FICO_RANGE = [
  { start: 500, display: '500 - 579' },
  { start: 580, display: '580 - 619' },
  { start: 620, display: '620 - 639' },
  { start: 640, display: '640 - 659' },
  { start: 660, display: '660 - 679' },
  { start: 680, display: '680+' },
];

type QuoteViewData = QuoteData & {
  id: string;
  runDate: string;
  generateId: string;
  leadId: number | string;
  importId: string | null;
};

type TableDataType = 'currency' | 'percent' | 'string' | 'number';
type TableRowVariant =
  | 'header-row'
  | 'fw500-td'
  | 'header-row callout'
  | 'header small'
  | 'apr'
  | 'header bold'
  | 'row-label'
  | 'spacer'
  | 'separate'
  | 'header small bold';

interface TableRowOptions {
  dataType?: TableDataType;
  decimalScale?: number;
  variant?: TableRowVariant;
  visible?: boolean;
}

interface TableRow {
  key: string;
  data?: Array<string | number>;
  value?: string | number;
  options?: TableRowOptions;
}

const loanDetails = (data: QuoteViewData): TableRow[] => [
  {
    key: 'Loan Details',
    options: { variant: 'header-row' },
  },
  {
    key: data.isIrrrl ? 'Estimated Value' : 'Approximate Value',
    value: data?.value.toFixed(),
    options: { dataType: 'currency', variant: 'fw500-td' },
  },
  {
    key: data?.purpose === 'Purchase' ? 'Loan Amount' : 'Current Balance',
    value: data?.balance || 0,
    options: { dataType: 'currency', variant: 'fw500-td' },
  },
  {
    key: data?.purpose !== 'Purchase' ? 'Available Cash Out' : '',
    value: parseInt((data.value * 0.975 - data?.balance).toString()),
    options: {
      dataType: 'currency',
      variant: 'fw500-td',
      visible: false && data.purpose !== 'Purchase' && data.value * 0.975 - data?.balance > 10000,
    },
  },
  {
    key: 'State',
    value: data?.state || 'AK',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Loan Purpose',
    value: data?.purpose || 'Refinance',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Lock Term',
    value: '30 Days',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Occupancy',
    value: data.occupancy || 'Primary',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Property Type',
    value: data.structure || 'Single Family',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Credit Range',
    value: FICO_RANGE.findLast((range) => data?.fico > range.start)?.display,
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Escrow Waiver',
    value: 'Available',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Please verify the accuracy of your loan details',
    options: { variant: 'header-row callout' },
  },
];

const titleData = (data: QuoteViewData): TableRow[] => [
  {
    key: 'Lender & Third Party Fees',
    options: { variant: 'header-row' },
  },
  {
    key: 'Underwriting',
    value: 'Waived',
    options: { variant: 'fw500-td' },
  },
  {
    key: 'Processing',
    value: data.processing === 0 ? 'Waived' : data.processing,
    options: { dataType: data.processing === 0 ? 'string' : 'currency', variant: 'fw500-td' },
  },
  {
    key: 'Appraisal (if Required)',
    value: data.appraisalEstimate.toFixed(),
    options: { dataType: 'currency', variant: 'fw500-td' },
  },
  {
    key: 'Credit & Verifications',
    value: data.creditFee,
    options: { dataType: 'currency', variant: 'fw500-td' },
  },
  {
    key: 'Title Charges (Services You Can Shop For)',
    options: { variant: 'header-row' },
  },
  {
    key: 'Estimated Title Charges & Services',
    value: data.titleFees || 2400.75,
    options: { dataType: 'currency', variant: 'fw500-td' },
  },
  {
    key: 'Total Fees in Closing Costs',
    value: data.totalClosingCostLoan,
    options: { dataType: 'currency', variant: 'header small bold' },
  },
];

const optionsData = (data: QuoteViewData): TableRow[] => [
  {
    key: '',
    data: data.options.map((opt: QuoteOption) => opt.label || ''),
    options: { variant: 'header small' },
  },
  {
    key: 'Loan Type',
    data: data.options.map((opt: QuoteOption) => opt.program.toUpperCase()),
    options: { variant: 'header small' },
  },
  {
    key: 'Term',
    data: data.options.map((opt: QuoteOption) => opt.term),
    options: { variant: 'header small' },
  },
  {
    key: 'Loan Amount',
    data: data.options.map((opt: QuoteOption) => Number(opt.totalLoanAmount || 0).toFixed()),
    options: { dataType: 'currency' },
  },
  {
    key: 'Buy Down Pt',
    data: data.options.map(
      (opt: QuoteOption) => (opt.pointsOrCredit / opt.totalLoanAmount) * 100 || 0
    ),
    options: { dataType: 'number', decimalScale: 3 },
  },
  {
    key: 'APR',
    data: data.options.map((opt: QuoteOption) => opt.apr || 0),
    options: { dataType: 'percent', variant: 'apr' },
  },
  {
    key: 'Loan–to–value',
    data: data.options.map((opt: QuoteOption) => opt.ltv || 0),
    options: { dataType: 'percent', decimalScale: 2 },
  },
  {
    key: 'Interest Rate',
    data: data.options.map((opt: QuoteOption) => opt.rate || 0),
    options: { dataType: 'percent', variant: 'header bold' },
  },
  {
    key: 'Monthly Payment Details',
    options: { variant: 'row-label' },
  },
  {
    key: 'Principal and Interest Payment',
    data: data.options.map((opt: QuoteOption) => (opt.monthlyPI || 0).toFixed(2)),
    options: { dataType: 'currency' },
  },
  {
    key: 'Est. Monthly Escrow (Tax / Insurance)',
    data: data.options.map((opt: QuoteOption) => opt.escrowMonthly || 0),
    options: { dataType: 'currency' },
  },
  {
    key: 'PMI',
    data: data.options.map((opt: QuoteOption) => Number(opt.pmi || 0).toFixed(2)),
    options: { dataType: 'currency', visible: data.loanProgram !== 'va' },
  },
  {
    key: 'Estimated Monthly Payment',
    data: data.options.map((opt: QuoteOption) =>
      Number(opt.monthlyPI + opt.escrowMonthly + opt.pmi || 0).toFixed(2)
    ),
    options: { variant: 'header bold', dataType: 'currency' },
  },
  {
    key: '',
    options: { variant: 'spacer' },
  },
  {
    key: data.purpose === 'Purchase' ? 'Needed at Closing' : 'Cash Out',
    data: data.options.map((opt: QuoteOption) => Number(opt.cashOutAmount).toFixed()),
    options: { variant: 'separate', dataType: 'currency' },
  },
  {
    key: '',
    options: { variant: 'spacer' },
  },
];

const TableCell = ({
  cellData,
  options,
}: {
  cellData: string | number;
  options?: TableRowOptions;
}) => {
  return (
    <Table.Td p={0}>
      {options?.dataType === 'currency' ? (
        <NumberFormatter value={cellData} thousandSeparator prefix="$" />
      ) : options?.dataType === 'percent' ? (
        <NumberFormatter
          value={Number(cellData).toFixed(options.decimalScale || 3)}
          thousandSeparator
          suffix="%"
          decimalScale={3}
        />
      ) : options?.dataType === 'number' ? (
        <NumberFormatter
          value={Number(cellData).toFixed(options.decimalScale || 3)}
          thousandSeparator
          decimalScale={3}
        />
      ) : (
        cellData
      )}
    </Table.Td>
  );
};
const TableComponent = ({ tableData, variant }: { tableData: TableRow[]; variant: string }) => {
  const dataWithData = tableData.find((d) => (d.data && Array.isArray(d.data)) || d.value);

  const numCols = Array.isArray(dataWithData?.data)
    ? dataWithData.data.length + 1
    : typeof dataWithData?.value === 'string' || typeof dataWithData?.value === 'number'
      ? 2
      : 1;

  return (
    <Table
      ta="center"
      variant="vertical"
      withTableBorder={variant !== 'quote'}
      withColumnBorders
      // tabularNums
      className={`${classes.table}, ${classes[variant]}`}
      classNames={{
        table: `options-table ${variant}`,
        th: 'options-table-head',
      }}
    >
      <Table.Tbody className={classes.tbody}>
        {tableData.map(({ key, data, options, value }: TableRow, i) => {
          const isFullRow = ['row-label', 'header-row', 'header-row callout'].includes(
            options?.variant ?? ''
          );
          if (options?.visible === false) return;
          return (
            <Table.Tr
              key={`${key}-${i}`}
              className={
                options?.variant
                  ? `${options?.variant
                      .split(' ')
                      .map((v: string) => classes[v])
                      .join(' ')}`
                  : ''
              }
            >
              <Table.Th colSpan={isFullRow ? numCols : 1} ta={isFullRow ? 'center' : 'right'}>
                {key}
              </Table.Th>
              {data && Array.isArray(data) ? (
                data.map((d, j) => (
                  <TableCell key={`${key}-${i}-${j}`} cellData={d} options={options} />
                ))
              ) : value ? (
                <TableCell cellData={value} options={options} />
              ) : null}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};

const parseJwt = <T,>(token: string): T => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join('')
  );

  return JSON.parse(jsonPayload) as T;
};

// ?d=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6InF1b3RlXzAxanI0bnlqMmZmZXlyMWgydmY2eDAxdHhnIiwibGVhZElkIjoibGVhZF8wMWpyNG55ajJmZmV5cjFoMnA0azJ6Nmt5YSIsImltcG9ydElkIjoiaW1fdHJpZ2dlcl8wMWpyMGZ5OG45Znpzdm1nOTcwN3Y1YTR3aiIsImxvSWQiOjI2MzYwNjcsImJmbiI6IlJpY2FyZWRvIiwiYmxuIjoiTGlndXRvbSIsInZhbHVlIjo5NDg0MDAsImJhbGFuY2UiOjI3MzY3MywiY2FzaE91dCI6NTAwMDAsInN0YXRlIjoiQ0EiLCJmaWNvIjo3MzksInNlbGVjdGVkQlBTIjoyMjUsImJhbGFuY2VBbmRDYXNoIjozMjM2NzMsImZsYXREb2xsYXIiOjcyODI2NC4yNSwibWF4TFRWIjowLjgsIm9wdGlvbnMiOlt7ImxvYW5Qcm9ncmFtIjoiRkhBIn1dLCJpYXQiOjE3NDM5MTM5NjF9.RHoZfrWcCkOsONXK5jUuJjn0dNyKfvBEOVY0Ogfdphc
export const SlimQuote = () => {
  const [searchParams, _setSearchParams] = useSearchParams();
  const [importRecord, setImportRecord] = useState<IImport | null>();

  // Get a specific query parameter
  const quoteId = 'quoteIdForLookingUp'; // searchParams.get('id');
  const importIdParam = searchParams.get('import');
  const quoteDataParam = searchParams.get('d');

  const quotedData = useMemo<QuoteViewData>(() => {
    if (quoteDataParam) {
      return parseJwt<QuoteViewData>(quoteDataParam);
    }

    const {
      bfn,
      bln,
      state,
      fico,
      value,
      balance,
      fhaLimit,
      runDate,
      generateId,
      leadId,
      importId,
      loId,
      loEmail,
      loName,
      zip,
      street,
      currentType,
      loPhone,
      titleFees,
      hoiMonthly,
      taxesMonthly,
      isIrrrl,
      selectedBPS,
    } = {
      bfn: 'Jeremy',
      bln: 'Hicks',
      state: 'TN',
      fico: 750,
      value: 967859,
      balance: 701073,
      fhaLimit: 800000,
      runDate: '2025-06-01',
      generateId: '12345',
      leadId: 12345,
      importId: importIdParam,
      loId: '2636067',
      loEmail: 'jhicks@emortgagecapital.com',
      loName: 'Jeremy Hicks',
      zip: 37128,
      street: '520 Sapphire Dr',
      currentType: 'VA',
      loPhone: '(321) 654-0987',
      titleFees: 1492,
      hoiMonthly: 150,
      taxesMonthly: 400,
      isIrrrl: false,
      selectedBPS: 225,
    };

    const q1 = new QuoteModel({
      bfn,
      bln,
      state,
      fico,
      balance,
      value,
      fhaLimit,
      zip,
      street,
      rateSheet: importRecord?.rates || [],
      currentType,
      titleFees,
      hoiMonthly,
      taxesMonthly,
      loEmail,
      loName,
      loPhone,
      loId,
      selectedBPS,
      isIrrrl,
    });

    return {
      id: quoteId,
      runDate,
      generateId,
      leadId,
      importId,
      ...q1.compute(),
    } satisfies QuoteViewData;
  }, [importRecord, quoteDataParam]);

  useEffect(() => {
    console.log('quotedData', quotedData);
    // console.log('quotedData canQuote', quotedData.canQuote);
    // console.log('quotedData - Option 1', quotedData.options[0]);
  }, [quotedData]);

  useEffect(() => {
    async function onLoad(importId: string) {
      try {
        const { body } = await refreshImport(importId).response;
        const apiImport: IImport = (await body.json()) as unknown as IImport;

        if (apiImport) {
          setImportRecord({ ...apiImport });
        }
      } catch (e) {
        // onError(e);
      }
    }

    if (!quoteDataParam) {
      onLoad(quotedData.importId as string);
    }
  }, []);

  return (
    <>
      {window.top !== window.self && (
        <Button variant="subtle" size="xs" onClick={window.print}>
          Print
        </Button>
      )}
      <Stack align="stretch" justify="center" gap={0} miw={800} maw={900} className={classes.root}>
        <Grid>
          <Grid.Col span={5}>
            <Stack gap={0}>
              <img src="/emc-logo-full.png" width={288} alt="" />
              <Text c="#1d3064" size="lg" fw="bold" mt={40} mb={-6} w={550}>
                {quotedData?.street}, {quotedData?.zip}
              </Text>
              <Text size="sm" fs="italic">
                {formatInTimeZone(new Date(), 'America/Los_Angeles', 'MM/dd/yy p z')}
              </Text>
            </Stack>
          </Grid.Col>
          <Grid.Col span={4}>
            <Group justify="left" pl={50}>
              <List
                center
                c="#1d3064"
                icon={
                  <ThemeIcon size={24} variant="white" c="#1d3064">
                    <IconCheck />
                  </ThemeIcon>
                }
              >
                <List.Item>
                  <Text fs="italic">No Application Fees</Text>
                </List.Item>
                {quotedData.processing === 0 ? (
                  <List.Item>
                    <Text fs="italic">No Loan Processing Fees</Text>
                  </List.Item>
                ) : null}
                <List.Item>
                  <Text fs="italic">No Underwriting Fees</Text>
                </List.Item>
                {quotedData.isIrrrl && (
                  <>
                    <List.Item>
                      <Text fs="italic">No Income Docs</Text>
                    </List.Item>
                    <List.Item>
                      <Text fs="italic">No Appraisal</Text>
                    </List.Item>
                  </>
                )}
                <List.Item>
                  <Text fs="italic">Fast Closings</Text>
                </List.Item>
              </List>
            </Group>
          </Grid.Col>
          <Grid.Col span={3} ta="right">
            <Text c="#1d3064" fw="bold" size="xl">
              {quotedData.loName}
            </Text>
            <Text size="sm">Sr. Loan Officer</Text>
            <Text size="sm">NMLS #{quotedData.loId}</Text>
            <Text c="#1d3064" size="md" fw="bold">
              {quotedData.loPhone}
            </Text>
            <Text size="sm">
              <a href={`mailto:${quotedData.loEmail}`}>{quotedData.loEmail}</a>
            </Text>
          </Grid.Col>
        </Grid>
        <Group className={classes.callout}>
          <TableComponent variant="quote" tableData={optionsData(quotedData)} />
        </Group>
        <Grid justify="space-between">
          <Grid.Col span={5}>
            {/* <TableComponent variant="details" tableData={titleData(quotedData)} /> */}
            <List size="xs" w="150%" mt={10} c="var(--mantine-color-gray-8)">
              You are Pre-Qualified for the above options. This is not a commitment to lend. Rates
              and terms are subject to change based on market conditions and borrower eligibility.
              Upon application, a loan estimate will be provided. This offer is subject to
              verification of borrower qualifications, property evaluations and credit approval.
              Information used in connection with this offer was derived from public record. All
              loans are subject to credit and property approval. Interest rates and APR's are
              subject to change at any time. Escrow payment of taxes and insuracne may be required.
              Appraisal waivers subject to Fannie Mae, Freddie Mac or VA approval. Available cash
              out based on an approximate value. In refinancing your loan, total finance charges may
              be higher over the life of the loan. Rates and APR can vary based on criteria in the
              Loan Details section.
              {quotedData.bulletNote &&
                Array.isArray(quotedData.bulletNote) &&
                quotedData.bulletNote.map((note: string, i: number) => (
                  <span key={`bullet-note-${i}`}>{note}</span>
                ))}
            </List>
          </Grid.Col>
          <Grid.Col span={4}>
            <TableComponent variant="details" tableData={loanDetails(quotedData)} />
          </Grid.Col>
        </Grid>
        <section className={classes.reviews}>
          <img src="/ratings.jpg" alt="" />
        </section>
        <footer className={classes.footer}>
          <img src="/footer.jpg" alt="" />
        </footer>
      </Stack>
    </>
  );
};
